import { useEffect, useRef, useState } from "react";
import http from "@/lib/api";
import { safeStorage } from "@/lib/safeStorage";
import { MessageCircle, X, Send, ShieldCheck, CheckCircle2 } from "lucide-react";
import VadimBookingCard from "@/components/VadimBookingCard";
import ElevenLabsWidget from "@/components/ElevenLabsWidget";
import { Mic, PenLine } from "lucide-react";

const SID_KEY = "bkt_vadim_sid";
const LANG_KEY = "bkt_vadim_lang";
const T = {
  no: { hello: "Hei! Jeg er Vadim, kundeservice hos Bergen Kaffemaskin- og Teknikk Service. Beskriv problemet ditt, still et spørsmål om priser, bestill tid for innlevering eller spør om status på reparasjonen din.",
    ph: "Skriv en melding …", sub: "Kundeservice · svarer på norsk, русский, English", verified: "Identitet bekreftet", booked: "Forespørsel mottatt", workshop: "Svar fra verkstedet", err: "Beklager, prøv igjen om litt eller ring 90 23 22 06.", launch: "Chat med oss" },
  ru: { hello: "Здравствуйте! Я Вадим, служба поддержки Bergen Kaffemaskin- og Teknikk Service. Опишите проблему, спросите о ценах, запишитесь на сдачу машины или узнайте статус ремонта.",
    ph: "Напишите сообщение …", sub: "Поддержка · norsk, русский, English", verified: "Личность подтверждена", booked: "Запрос получен", workshop: "Ответ мастерской", err: "Извините, попробуйте чуть позже или позвоните: 90 23 22 06.", launch: "Чат" },
  en: { hello: "Hi! I'm Vadim, customer service at Bergen Kaffemaskin- og Teknikk Service. Describe your problem, ask about prices, book a drop-off time or ask about the status of your repair.",
    ph: "Type a message …", sub: "Customer service · norsk, русский, English", verified: "Identity verified", booked: "Request received", workshop: "Reply from the workshop", err: "Sorry, please try again shortly or call 90 23 22 06.", launch: "Chat with us" },
};

export function openVadimChat(kind = "menu") { window.dispatchEvent(new CustomEvent("vadim:open", { detail: kind })); }

function VadimMenu({ onText, onVoice, onClose }) {
  return (
    <div data-testid="vadim-menu" className="fixed z-[1100] right-4 bottom-4 sm:right-5 sm:bottom-5 w-[min(92vw,320px)] rounded-2xl bg-white shadow-2xl border border-[#D4A574]/40 overflow-hidden">
      <div className="bg-[#3D2314] text-white px-4 py-3 flex items-start gap-3">
        <div className="h-9 w-9 rounded-full bg-[#D4A574] text-[#3D2314] font-bold flex items-center justify-center font-display">V</div>
        <div className="flex-1">
          <div className="font-semibold leading-tight">Vadim – BKT kundehjelp</div>
          <div className="text-xs text-white/70">Hvordan kan jeg hjelpe deg?</div>
        </div>
        <button onClick={onClose} className="p-1 text-white/70 hover:text-white" data-testid="vadim-menu-close"><X className="h-5 w-5" /></button>
      </div>
      <div className="p-3 space-y-2">
        <MenuBtn icon={PenLine} title="Skriv" sub="Send en melding til Vadim" onClick={onText} id="vadim-menu-text" />
        <MenuBtn icon={Mic} title="Snakk" sub="Start en samtale med Vadim" onClick={onVoice} id="vadim-menu-voice" />
        <p className="text-[11px] text-[#1A1A1A]/50 px-1">Vadim er vår digitale kundemedarbeider (AI).</p>
      </div>
    </div>
  );
}

function MenuBtn({ icon: Icon, title, sub, onClick, id }) {
  return (
    <button onClick={onClick} data-testid={id}
      className="w-full flex items-center gap-3 rounded-xl border border-[#D4A574]/40 px-3 py-3 text-left hover:bg-[#FFF8F0] hover:border-[#D4A574] transition-colors">
      <span className="h-9 w-9 rounded-full bg-[#FFF8F0] flex items-center justify-center"><Icon className="h-4 w-4 text-[#3D2314]" /></span>
      <span><span className="block font-semibold text-[#3D2314] text-sm">{title}</span><span className="block text-xs text-[#1A1A1A]/60">{sub}</span></span>
    </button>
  );
}

export default function VadimChat() {
  const [mode, setMode] = useState(null);
  const open = mode === "text";
  const setOpen = (v) => setMode(v ? "text" : null);
  const [lang, setLang] = useState(safeStorage.get(LANG_KEY) || "no");
  const [state, setState] = useState({ messages: [], pending_booking: null, booking: null, verified: false });
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const t = T[lang] || T.no;

  useEffect(() => {
    const h = (e) => setMode(e.detail || "menu");
    window.addEventListener("vadim:open", h);
    if (new URLSearchParams(window.location.search).get("chat") === "1") {
      setMode("menu");
    }
    return () => window.removeEventListener("vadim:open", h);
  }, []);
  useEffect(() => {
    const sid = safeStorage.get(SID_KEY);
    if (open && sid && !state.session_id) http.get(`/v1/public/chat/${sid}`).then((r) => setState(r.data)).catch(() => safeStorage.remove(SID_KEY));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [state.messages, busy, open]);
  useEffect(() => { document.body.classList.toggle("vadim-open", open); }, [open]);
  useEffect(() => {
    if (!open || !state.handoff || !state.session_id) return;
    const iv = setInterval(() => {
      if (!busy) http.get(`/v1/public/chat/${state.session_id}`).then((r) => {
        if (r.data.messages.length !== state.messages.length) setState(r.data);
      }).catch(() => {});
    }, 20000);
    return () => clearInterval(iv);
  }, [open, state.handoff, state.session_id, state.messages.length, busy]);

  const apply = (d) => {
    safeStorage.set(SID_KEY, d.session_id);
    setState(d);
    if (d.language && d.language !== lang) { setLang(d.language); safeStorage.set(LANG_KEY, d.language); }
  };
  const chooseLang = (l) => { setLang(l); safeStorage.set(LANG_KEY, l); };

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setState((s) => ({ ...s, messages: [...s.messages, { role: "user", content: text }] }));
    try {
      const r = await http.post("/v1/public/chat", { session_id: state.session_id || safeStorage.get(SID_KEY) || "", message: text, language: lang });
      apply(r.data);
    } catch (e) {
      const msg = T[lang]?.err || T.no.err;
      setState((s) => ({ ...s, messages: [...s.messages, { role: "assistant", content: msg }] }));
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try { apply((await http.post("/v1/public/chat/confirm-booking", { session_id: state.session_id })).data); }
    catch { /* keep state */ } finally { setBusy(false); }
  };

  if (mode === "menu") return <VadimMenu onText={() => setMode("text")} onVoice={() => setMode("voice")} onClose={() => setMode(null)} />;
  if (mode === "voice") return (
    <>
      <ElevenLabsWidget autoStart />
      <button onClick={() => setMode(null)} data-testid="vadim-voice-close"
        className="fixed z-[1100] right-4 sm:right-5 top-20 flex items-center gap-1.5 rounded-full bg-white border border-[#D4A574]/50 text-[#3D2314] text-xs font-semibold px-3 py-2 shadow-lg hover:bg-[#FFF8F0]">
        <X className="h-4 w-4" />Lukk samtalevinduet
      </button>
    </>
  );
  if (!open) return (
    <button onClick={() => setMode("menu")} data-testid="vadim-chat-launcher" aria-label="Vadim – BKT kundehjelp"
      className="fixed bottom-4 right-4 sm:bottom-5 sm:right-5 z-[1100] flex items-center gap-2 rounded-full bg-[#D4A574] text-[#3D2314] ring-2 ring-white pl-4 pr-5 py-3 shadow-xl hover:bg-[#E0B485] hover:-translate-y-0.5 transition-[transform,background-color]">
      <MessageCircle className="h-5 w-5 text-[#3D2314]" /><span className="text-sm font-semibold">{t.launch}</span>
    </button>
  );

  return (
    <div data-testid="vadim-chat-panel"
      className="fixed z-[1100] inset-x-2 bottom-2 top-16 sm:inset-auto sm:right-5 sm:bottom-5 sm:w-[380px] sm:h-[600px] flex flex-col rounded-2xl bg-white shadow-2xl border border-[#D4A574]/40 overflow-hidden">
      <div className="bg-[#3D2314] text-white px-4 py-3 flex items-center gap-3">
        <div className="h-9 w-9 rounded-full bg-[#D4A574] text-[#3D2314] font-bold flex items-center justify-center font-display">V</div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold leading-tight">Vadim</div>
          <div className="text-[11px] text-white/60 truncate">{t.sub}</div>
        </div>
        <div className="flex rounded-full bg-white/10 p-0.5" data-testid="vadim-lang-switch">
          {["no", "ru", "en"].map((l) => (
            <button key={l} onClick={() => chooseLang(l)} data-testid={`vadim-lang-${l}`}
              className={`px-2 py-0.5 text-[11px] rounded-full uppercase transition-colors ${lang === l ? "bg-[#D4A574] text-[#3D2314] font-semibold" : "text-white/70"}`}>{l}</button>
          ))}
        </div>
        <button onClick={() => setOpen(false)} className="p-1 text-white/70 hover:text-white" data-testid="vadim-chat-close"><X className="h-5 w-5" /></button>
      </div>
      {(state.verified || state.booking) && (
        <div className="px-3 py-1.5 bg-[#FFF8F0] border-b border-[#D4A574]/30 flex gap-3 text-[11px] text-[#3D2314]">
          {state.verified && <span className="flex items-center gap-1" data-testid="vadim-verified-badge"><ShieldCheck className="h-3.5 w-3.5" />{t.verified}</span>}
          {state.booking && <span className="flex items-center gap-1" data-testid="vadim-booked-badge"><CheckCircle2 className="h-3.5 w-3.5" />{t.booked}</span>}
        </div>
      )}
      <div className="flex-1 overflow-y-auto py-3 space-y-2 bg-[#FBF7F2]" data-testid="vadim-chat-messages">
        <Bubble role="assistant" text={t.hello} />
        {state.messages.map((m, i) => <Bubble key={i} role={m.role} text={m.content} idx={i} workshop={m.from_workshop ? t.workshop : ""} />)}
        {state.pending_booking && !state.booking && <VadimBookingCard draft={state.pending_booking} lang={lang} busy={busy} onConfirm={confirm} />}
        {busy && <div className="px-4 text-[#3D2314]/50 text-sm" data-testid="vadim-typing"><span className="animate-pulse">● ● ●</span></div>}
        <div ref={endRef} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-[#D4A574]/30 p-2 flex gap-2 bg-white">
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder={t.ph} maxLength={2000} data-testid="vadim-chat-input"
          className="flex-1 rounded-full border border-[#D4A574]/50 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A574]" />
        <button type="submit" disabled={busy || !input.trim()} data-testid="vadim-chat-send"
          className="h-10 w-10 rounded-full bg-[#3D2314] text-white flex items-center justify-center disabled:opacity-40 hover:bg-[#3D2314]/90 transition-colors">
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
}

function Bubble({ role, text, idx, workshop }) {
  const mine = role === "user";
  return (
    <div className={`flex px-3 ${mine ? "justify-end" : "justify-start"}`} data-testid={idx === undefined ? "vadim-greeting" : `vadim-msg-${role}-${idx}`}>
      <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap leading-relaxed ${mine ? "bg-[#3D2314] text-white rounded-br-sm" : "bg-white text-[#1A1A1A] border border-[#D4A574]/30 rounded-bl-sm"}`}>
        {workshop && <div className="text-[10px] uppercase tracking-wide text-[#D4A574] font-semibold mb-0.5" data-testid={`vadim-workshop-label-${idx}`}>{workshop}</div>}
        {text}
      </div>
    </div>
  );
}
